// src/pages/PresentationGenerator.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, LinearProgress, Box
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";

const defaultVizPrompt = 
`Deck plan (works for odd or even num_slides):
• First slide — Big idea: one-sentence statement distilled from lesson_content + a 2–3 bullet roadmap of subtopics from lesson_content.
• Middle slides — Alternate:
  – Key Term slide: heading = a term from lesson_content; 2–3 bullets: (1) concise definition, (2) one property/use-case — both from lesson_content.
  – Concept Link slide: heading = "Term A ↔ Term B"; 2–3 bullets: (1) how they relate, (2) a tiny example — strictly from lesson_content.
• Final slide — Summary map: 3–4 bullets showing how the terms connect to the big idea, wording drawn from lesson_content.
`


/* --- Modal just handles form state --- */
function PresentationGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  // NEW: keep the text in state so it can be edited, initialText is ignored
  const [textToVisualize, setTextToVisualize] = useState("");
  const [vizType, setVizType] = useState("presenation");
  const [vizSubtype, setVizSubtype] = useState("orientation");
  const [vizName, setVizName] = useState("presentation");
  const [vizPrompt, setVizPrompt] = useState(defaultVizPrompt);
  
  const [loading, setLoading] = useState(false);
  const [numSlides, setNumSlides] = useState(18); 

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onGenerate(textToVisualize, { vizName, vizType, vizSubtype, vizPrompt, numSlides });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Presentation Deck Builder</DialogTitle>
      <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column" }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}


        {/* sticky controls */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            pt: 2,
            pb: 1,
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <TextField
            fullWidth
            label="Visualization Name"
            value={vizName}
            onChange={e => setVizName(e.target.value)}
            margin="normal"
          />
          {vizSubtype === "custom" && (<TextField
            fullWidth
            multiline
            rows={8}
            label="Visualization Prompt"
            value={vizPrompt}
            onChange={e => setVizPrompt(e.target.value)}
            margin="normal"
          />
          )}

          <TextField
            select
            label="Presentation Type"
            fullWidth
            value={vizSubtype}
            onChange={e => setVizSubtype(e.target.value)}
            margin="normal"
          >
            <MenuItem value="orientation">Quick Overview (No Diagrams)</MenuItem>
            <MenuItem value="explainer">Explainer Slides – With Diagrams</MenuItem> 
            <MenuItem value="custom">Custom Presentation Deck</MenuItem>            
            <MenuItem value="placeholder">Placeholder</MenuItem>
          </TextField>
          <TextField
            fullWidth
            type="number"
            value={numSlides}
            onChange={(e) => setNumSlides(parseInt(e.target.value, 10))}
            label="Number of Slides"
            variant="outlined"
            margin="normal"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PresentationGenerator() {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (text, courseId, lessonId, { vizName, vizType, vizSubtype, vizPrompt, numSlides }) => {
      const body = {
        course_id: courseId,
        lesson_id: lessonId,
        visual_type: vizType,
        visual_subtype: vizSubtype,
        name: vizName,
        custom_prompt: vizSubtype == "custom" ? vizPrompt: "",
        num_slides: numSlides
      };
      const { data } = await axios.post("presentations/lesson_manual/", body);
      return data;
    },
    [axios]
  );

  return (
    <ContentBuilderPage
      instructionText="The lesson content will be used for presentation deck."
      modal={<PresentationGeneratorModal />}
      generatorFn={generateVisualization}
      assetType="visuals"
      createBtnTxt="Create Presentation Deck"
    />
  );
}

